<template>
    <kt-modal 
        :visible="recordVisible" 
        :title="recordTitle"
        :size="'modal-xl'" 
        @dismiss="onDismiss()"
    >
        <template #body>
            <work-order-form v-model="record"></work-order-form>
        </template>

        <template #footer>
            <div class="kt-modal-actions d-flex align-items-center justify-content-between w-100">
                <div class="form-check form-switch">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="sendNotificationToggleWO"
                        v-model="sendNotification"
                    >
                    <label class="form-check-label" for="sendNotificationToggleWO">
                        Send Update Notification
                    </label>
                </div>
                <div>
                    <button @click="onDismiss()" class="btn btn-outline-secondary">
                        <X :size="16" class="me-1" />Close
                    </button>
                    <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger ms-2">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                    <button @click="saveRecord()" class="btn btn-primary ms-2">
                        <Save :size="16" class="me-1" />Save
                    </button>
                </div>
            </div>
        </template>
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Api from "@/services/ApiService";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import Emitter from "@/services/Emitter";
import { WorkOrderModel } from "@/models/WorkOrderModel";
import KtModal from "@/components/UI/KtModal.vue";
import WorkOrderForm from "@/components/WorkOrders/WorkOrderForm.vue";
import { X, Trash2, Save } from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Invoice"); //< Client Document
const recordVisible = ref(true);
const unwatch: Array<Function> = [];

const props = defineProps<{
    id?: number | null;
}>();

const emit = defineEmits(['close']);

const record = ref<WorkOrderModel>(new WorkOrderModel());
const sendNotification = ref(false);

const onDismiss = () => {
    // Check if we're in a route-based context or global modal
    if (route.params.id && route.name) {
        recordVisible.value = false;
        setTimeout(() => {
            router.push({ path: "/workorders" });
        }, 100);
    } else {
        // Global modal - emit close event
        emit('close');
    }
};

const saveRecord = async () => {
    try {
        // Validate
        if (!record.value.ContactId) {
            Alert.notify({ text: "Please select a client", type: "error" });
            return;
        }

        // Auto-generate invoice number if not provided
        if (!record.value.InvoiceNumber && record.value.Contact && record.value.Id) {
            const contactCode = record.value.Contact.Code || 'INV';
            record.value.InvoiceNumber = `${contactCode}-${record.value.Id}`;
        }

        // Save
        const saved = await Api.saveWorkOrder(record.value);
        
        // Auto-generate invoice number for new records (after first save)
        if (!saved.InvoiceNumber && saved.Contact && saved.Id) {
            const contactCode = saved.Contact.Code || 'INV';
            saved.InvoiceNumber = `${contactCode}-${saved.Id}`;
            // Save again with the generated invoice number
            await Api.saveWorkOrder(saved);
        }
        
        // Reload the full work order with all relationships (Items, Contact, etc.)
        // This ensures the model has complete data for printing/PDF generation
        if (saved.Id) {
            const fullRecord = await Api.loadWorkOrder(saved.Id);
            record.value = fullRecord;
        } else {
            record.value = saved;
        }
        
        Toast.success("Work order saved successfully");
        
        // Refresh datatable if embedded
        Emitter.emit('datatable-refresh', 'workorder-list');
        Emitter.emit('datatable-refresh', 'workorder-embedded');
        
        // Update route if it was a new record
        if (route.params.id === 'new') {
            router.replace({ path: "/workorder/edit/" + saved.Id });
        }
    } catch (error) {
        console.error("Error saving work order:", error);
        Alert.notify({ text: "Failed to save work order", type: "error" });
    }
};

const deleteRecord = () => {
    Alert.confirm(
        'Are you sure you want to delete this work order?',
        async () => {
            try {
                await Api.deleteWorkOrder(record.value.Id!);
                Toast.success("Work order deleted");
                
                // Refresh datatables
                Emitter.emit('datatable-refresh', 'workorder-list');
                Emitter.emit('datatable-refresh', 'workorder-embedded');
                
                onDismiss();
            } catch (error) {
                console.error("Error deleting work order:", error);
                Alert.notify({ text: "Failed to delete work order", type: "error" });
            }
        },
        () => {} // No action on cancel
    );
};

// Watch for route param changes
unwatch.push(
    watch(
        () => route.params.id,
        async (newId) => {
            if (newId === 'new') {
                recordTitle.value = "New Invoice"; // was New Client Document
                record.value = new WorkOrderModel();
            } else if (newId) {
                try {
                    const data = await Api.loadWorkOrder(Number(newId));
                    record.value = data;
                    const type = data?.Title || 'Invoice';
                    recordTitle.value = `${type} #${data.InvoiceNumber || data.Id}`;
                } catch (error) {
                    console.error("Error loading record:", error);
                    Alert.notify({ text: "Failed to load record", type: "error" });
                }
            }
        },
        { immediate: true }
    )
);

// Watch for prop id changes (when opened as modal from global context)
unwatch.push(
    watch(
        () => props.id,
        async (newId) => {
            if (newId) {
                try {
                    const data = await Api.loadWorkOrder(Number(newId));
                    record.value = data;
                    const type = data?.Title || 'Invoice';
                    recordTitle.value = `${type} #${data.InvoiceNumber || data.Id}`;
                } catch (error) {
                    console.error("Error loading record:", error);
                    Alert.notify({ text: "Failed to load record", type: "error" });
                }
            } else if (newId === null) {
                // New record
                recordTitle.value = "New Invoice";
                record.value = new WorkOrderModel();
            }
        },
        { immediate: true }
    )
);

onUnmounted(() => {
    unwatch.forEach(fn => fn());
});
</script>
